import { get, post } from "./client";

// ============================================================================
// Customer Support — website-users panel (DEV-2056 FE / DEV-2208 BE contract).
// ============================================================================
//
// Wire contract, verified against the merged DEV-2208 controllers in
// `services/users-api/src/controllers/cs/website-users/` (and their models):
//
//   GET  /v1/cs/website-users/search?search=<q>&limit=<n>&offset=<n>
//        → `{ data: CsWebsiteUserSummary[], meta: { total, limit, offset } }`
//          (search/get.ts + models/websiteCsUsers.ts::searchWebsiteUsers)
//          `search` is OPTIONAL: omitted, the endpoint returns the unfiltered first
//          page, which is what the panel loads on mount. `offset` drives paging.
//   GET  /v1/cs/website-users/:userId/profile
//        → the profile object DIRECTLY (NO `{ data }` envelope).
//          [userId]/profile/get.ts does `response.end(JSON.stringify(profile))`,
//          so `getUser` does NOT unwrap `.data`.
//   GET  /v1/cs/website-users/:userId/audit
//        → `{ data: CsWebsiteAuditEntry[], meta: { limit, offset, actions } }`.
//          Note: the audit `meta` has NO `total` (it is not a CsListEnvelope) and
//          the rows are the full audit-log rows (actorId/targetUserId/metadata
//          included — [userId]/audit/get.ts returns `select()` rows).
//
// All timestamps are ISO strings (Date serialised via JSON). All keys camelCase.
// `client.ts` does NOT unwrap the envelope: `get<T>`/`post<T>` return
// `response.json()` typed as `T`, so each method unwraps here. `X-L500-Token` is
// attached automatically; no manual auth headers and no `organisationId`.
//
// Deviations adopted from the real controllers (spec Phase 1 differed):
//   - CsVerificationStatus: real values are
//     "verified" | "pending_deletion" | "deleted" | "anonymised"
//     (derived from website_users.status + anonymisedAt). The spec's
//     "unverified"/"rejected" do not exist; "anonymised" is real.
//   - CsWebsiteUserSummary.company / .accessTier are `string | null` (leftJoin +
//     derived role), not non-null.
//   - CsWebsiteUserProfile.company is a FLAT `string | null` (companyName), NOT a
//     `{ id, name }` object as the spec sketched. firstName/lastName/jobFunction/
//     country are all `string | null`.
//   - getUser returns the profile unwrapped (no `.data`), per above.
//   - CsWebsiteAuditEntry carries the full row (actorId/targetUserId/metadata,
//     `action` is a fixed union, `reason` is non-null).
//
// Profile fields added in the DEV-2056 review follow-up (backend adds them to
// `getWebsiteUserProfile` + the profile response schema):
//   emailVerifiedAt, failedLoginAttempts, lockedUntil, deletionReason,
//   deletionReasonText, deletionInitiatedBy, anonymisedAt, updatedAt, locale,
//   companyId.
// All of the date-ish ones are ISO strings (`string | null`), matching createdAt /
// lastLoginAt. `failedLoginAttempts` is a number and may legitimately be 0 — the UI
// must not treat 0 as "not set". The panel renders every one of these so a CS agent
// sees all the data we hold, rather than a subset.
//
// The `password/reset` and `deletion` support-action controllers (DEV-2056 BE follow-up)
// return `{ data: { status } }` and `{ data: { deletionScheduledAt, status } }` respectively —
// see controllers/cs/website-users/[userId]/password/reset/post.ts and .../deletion/post.ts.
// `status` for `password/reset` is one of "sent" | "not_eligible" | "suppressed_by_cooldown" |
// "send_failed" (the real outcome of dispatchPasswordReset, not the public endpoint's neutral
// 202). `status` for `deletion` is "scheduled" | "already_scheduled" (idempotent: a user already in
// pending_deletion keeps their existing schedule rather than restarting the 30-day clock).
//
// The `deletion/cancel` action takes the same `{ reason, reasonDetail? }` body as the other two
// and returns `{ data: { status } }`. It reverses a pending deletion — whether that deletion was
// initiated by staff or by the user themself — and restores the account to `verified`. It is
// idempotent, and only one of its three statuses is an actual reversal:
//   "cancelled"       — a pending deletion was found and reversed.
//   "already_deleted" — the purge has already run; there is nothing left to restore.
//   "not_pending"     — no deletion was scheduled, so nothing changed.
// The latter two are real 200 outcomes describing the state the account was already in, NOT
// errors, so callers must report them as that state rather than as a completed cancellation.
// The action also adds `cancel_deletion` to the audit action vocabulary, and the audit
// endpoint includes it in its default `actions=write` filter.
//
// The three actions do NOT share a reason vocabulary. Each controller validates `reason` against
// its own enum and rejects anything outside it with a 400, so the UI must offer only the reasons
// the chosen action accepts:
//   password/reset  → user_request, cannot_sign_in, other
//   deletion        → user_request, gdpr_erasure, bounced_email, duplicate, other
//   deletion/cancel → user_request, scheduled_in_error, other
// `user_request` and `other` are the only two common to all three. The split is what keeps the
// audit log coherent: one shared list would let an agent file a password reset — or a deletion
// *cancellation* — as "GDPR erasure", a self-contradictory row in the very log the reason field
// exists to make trustworthy.

// ---- Types -----------------------------------------------------------------

/**
 * Verification/lifecycle status of a website user, as derived by the backend
 * (models/websiteCsUsers.ts::deriveVerificationStatus).
 */
export type CsVerificationStatus = "verified" | "pending_deletion" | "deleted" | "anonymised";

/** Free-text access tier, derived from the user's first role grant (nullable). */
export type CsAccessTier = string;

/** A search-result row. `company`/`accessTier`/`deletionScheduledAt` are nullable. */
export type CsWebsiteUserSummary = {
  userId: string;
  fullName: string;
  email: string;
  company: string | null;
  verificationStatus: CsVerificationStatus;
  accessTier: CsAccessTier | null;
  deletionScheduledAt: string | null;
};

/** A single consent record — latest state per consent type. */
export type CsConsent = { granted: boolean; grantedAt: string | null };

/** Full read-only profile as returned (unwrapped) by the profile endpoint. */
export type CsWebsiteUserProfile = {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  company: string | null;
  jobFunction: string | null;
  jobFunctionOther: string | null;
  country: string | null;
  industry: string | null;
  accessTier: CsAccessTier | null;
  consents: { dataSharing: CsConsent; marketing: CsConsent; termsPrivacy: CsConsent };
  verificationStatus: CsVerificationStatus;
  isLegalBusinessEligible: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  deletionScheduledAt: string | null;
  deletionInitiatedActorType: "self" | "staff" | null;
  /** When the user confirmed their email address; null while unverified. */
  emailVerifiedAt: string | null;
  /** Consecutive failed sign-in attempts. 0 is a real value, not "unknown". */
  failedLoginAttempts: number | null;
  /** Lockout expiry after too many failed attempts; null when not locked. */
  lockedUntil: string | null;
  /** Coded deletion reason, and its free-text detail when the code is "other". */
  deletionReason: string | null;
  deletionReasonText: string | null;
  /** Actor id that initiated deletion (the actor *type* is deletionInitiatedActorType). */
  deletionInitiatedBy: string | null;
  /** Set once the record has been irreversibly anonymised. */
  anonymisedAt: string | null;
  updatedAt: string | null;
  locale: string | null;
  companyId: string | null;
  pendingCompanyId: string | null;
  firmRef: string | null;
};

/** One CS audit-log row (full row as returned by the audit endpoint). */
export type CsWebsiteAuditEntry = {
  id: string;
  actorId: string;
  actorEmail: string | null;
  targetUserId: string | null;
  action: "send_password_reset" | "delete_account" | "cancel_deletion" | "view_profile" | "search";
  reason: string;
  reasonDetail: string | null;
  metadata: unknown | null;
  createdAt: string;
};

// Allowed reasons per action, each list exactly the enum its own endpoint validates against
// (`other` requires free-text in all three).
export const CS_PASSWORD_RESET_REASONS = ["user_request", "cannot_sign_in", "other"] as const;
export const CS_DELETE_REASONS = [
  "user_request",
  "gdpr_erasure",
  "bounced_email",
  "duplicate",
  "other",
] as const;
export const CS_CANCEL_DELETION_REASONS = ["user_request", "scheduled_in_error", "other"] as const;

/** Every reason any action accepts; which subset is valid depends on the action. */
export type CsActionReason =
  | (typeof CS_PASSWORD_RESET_REASONS)[number]
  | (typeof CS_DELETE_REASONS)[number]
  | (typeof CS_CANCEL_DELETION_REASONS)[number];

/** Body for a guarded support action. */
export type CsActionInput = { reason: CsActionReason; reasonDetail?: string };

/** Standard paginated list envelope (search endpoint). */
export type CsListEnvelope<T> = {
  data: T[];
  meta: { total: number; limit: number; offset: number };
};

/** Outcome of dispatchPasswordReset, surfaced verbatim by the CS action (not the public
 * forgot-password endpoint's neutral 202). */
export type CsPasswordResetStatus =
  | "sent"
  | "not_eligible"
  | "suppressed_by_cooldown"
  | "send_failed";

/** Result of a staff-initiated password reset. */
export type CsPasswordResetResult = { status: CsPasswordResetStatus };

/** Whether this call scheduled a new deletion or found one already pending. */
export type CsDeleteStatus = "scheduled" | "already_scheduled";

/** Result of a staff-initiated account deletion (30-day soft-delete schedule, idempotent). */
export type CsDeleteResult = { deletionScheduledAt: string; status: CsDeleteStatus };

/**
 * Outcome of cancelling a scheduled deletion. Only "cancelled" reversed anything:
 * "already_deleted" means the purge has run and "not_pending" that none was scheduled.
 */
export type CsCancelDeletionStatus = "cancelled" | "already_deleted" | "not_pending";

/** Result of reversing a pending deletion (staff- or self-initiated), restoring `verified`. */
export type CsCancelDeletionResult = { status: CsCancelDeletionStatus };

// `client.ts` already prefixes API_BASE_URL (default "/api"), so paths start at /v1 —
// spelling the /api here again would resolve to /api/api/v1/... and 404.
const BASE_PATH = "/v1/cs/website-users";

// Backend caps `limit` at 100; asking for it explicitly keeps the cap visible here rather
// than leaving the panel on the controller's silent default of 25.
export const SEARCH_PAGE_SIZE = 100;

// ---- Service ---------------------------------------------------------------

export const customerSupportUsersService = {
  // Returns the whole envelope: the panel reads `meta.total` so a page is never presented
  // as the complete answer — the count line reports the current window against the true
  // total, and paging is offered whenever `total > limit`.
  // `search` is optional so the panel can load an unfiltered first page on mount.
  searchUsers: async ({
    search,
    limit = SEARCH_PAGE_SIZE,
    offset = 0,
  }: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<CsListEnvelope<CsWebsiteUserSummary>> => {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    // An empty term must be omitted entirely, not sent as `search=` — the backend treats a
    // present-but-blank filter differently from an absent one.
    if (search && search.trim() !== "") {
      params.set("search", search.trim());
    }
    return get<CsListEnvelope<CsWebsiteUserSummary>>(`${BASE_PATH}/search?${params.toString()}`);
  },

  // Profile endpoint returns the object directly — no `.data` unwrap.
  getUser: async (userId: string): Promise<CsWebsiteUserProfile> => {
    return get<CsWebsiteUserProfile>(`${BASE_PATH}/${userId}/profile`);
  },

  getUserAuditLog: async (userId: string): Promise<CsWebsiteAuditEntry[]> => {
    const response = await get<{ data: CsWebsiteAuditEntry[] }>(`${BASE_PATH}/${userId}/audit`);
    return response.data;
  },

  sendPasswordReset: async (
    userId: string,
    input: CsActionInput,
  ): Promise<CsPasswordResetResult> => {
    const response = await post<{ data: CsPasswordResetResult }>(
      `${BASE_PATH}/${userId}/password/reset`,
      input,
    );
    return response.data;
  },

  deleteUser: async (userId: string, input: CsActionInput): Promise<CsDeleteResult> => {
    const response = await post<{ data: CsDeleteResult }>(`${BASE_PATH}/${userId}/deletion`, input);
    return response.data;
  },

  cancelDeletion: async (userId: string, input: CsActionInput): Promise<CsCancelDeletionResult> => {
    const response = await post<{ data: CsCancelDeletionResult }>(
      `${BASE_PATH}/${userId}/deletion/cancel`,
      input,
    );
    return response.data;
  },
};
